"use client";

import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";

/**
 * Track the on-screen keyboard height inside Capacitor iOS / Android apps.
 *
 * iOS WKWebView does NOT shrink `100dvh` when the soft keyboard appears
 * (unlike Mobile Safari), so fixed-height chat layouts get their input row
 * hidden behind the keyboard. We listen to Capacitor's keyboard events and
 * expose the pixel height; callers subtract it from the container's height
 * (or add it as padding-bottom) so the input row floats just above the
 * keyboard the same way iMessage / WhatsApp do.
 *
 * Returns 0 on the web (no native keyboard plugin) so layouts stay normal.
 * Uses keyboardWillShow / keyboardWillHide for smooth animation, and resets
 * to 0 when the app returns from the background.
 */
export function useKeyboardHeight(): number {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!Capacitor.isPluginAvailable("Keyboard")) return;

    let removeShow: (() => void) | undefined;
    let removeHide: (() => void) | undefined;

    const showHandle = Keyboard.addListener("keyboardWillShow", (info) => {
      setHeight(info.keyboardHeight);
    });
    const hideHandle = Keyboard.addListener("keyboardWillHide", () => {
      setHeight(0);
    });

    void showHandle.then((h) => {
      removeShow = () => void h.remove();
    });
    void hideHandle.then((h) => {
      removeHide = () => void h.remove();
    });

    // Backgrounding the app dismisses the keyboard, but keyboardWillHide can
    // be lost while the WebView is suspended — the chat would come back
    // sized for a keyboard that is no longer there. Close it on the way out
    // and reset on the way back; a real reopen fires keyboardWillShow again.
    const onVisibility = () => {
      const el = document.activeElement;
      if (el instanceof HTMLElement && el.matches("input, textarea, [contenteditable]")) {
        el.blur();
      }
      if (document.visibilityState === "visible") {
        setHeight(0);
        // Body resize mode leaves the shrunk height inline if the hide
        // never reached the native side either.
        document.body.style.height = "";
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      removeShow?.();
      removeHide?.();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return height;
}
